import { ComponentProps, useEffect, useState } from "react"

import { CubeIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"

export type ImageProps = {
  src?: string
  className?: string
  /**
   * @deprecated Use `className` instead, or override fallback component with `children`.
   */
  fallbackClassName?: string
  children?: React.ReactNode
  onFinal?: () => void // Called when the image is loaded, errored or will never load (src is nil)
} & ComponentProps<"img">

const DefaultFallback = ({ className }: { className?: string }) => (
  <div
    className={twMerge(
      "flex h-full w-full items-center justify-center overflow-hidden bg-gray-100 text-white",
      className
    )}
  >
    <CubeIcon className="h-20 w-20 stroke-[0.5]" />
  </div>
)

/**
 * Thin wrapper component around `img` with fallback capabilities.
 *
 * @param props The image component props.
 * @param props.src The image source URL.
 * @param props.className The class name for the image.
 * @param props.fallbackClassName (deprecated) The class name for the fallback component.
 * @param props.children The fallback component to render if the image fails to load.
 * @returns The rendered image component.
 */
export function Image(props: ImageProps) {
  const { src, onFinal, children, className, fallbackClassName, ...restProps } = props
  const [hasError, setHasError] = useState<boolean>(false)

  const onError = () => {
    setHasError(true)
    onFinal?.()
  }

  useEffect(() => {
    if (src == null) {
      requestAnimationFrame(() => onFinal?.())
    }
  }, [src])

  return (
    <div className="contents">
      {src && !hasError ? (
        <img src={src} className={className} onError={onError} onLoad={() => onFinal?.()} {...restProps} />
      ) : children ? (
        <>{children}</>
      ) : (
        <DefaultFallback className={fallbackClassName ?? className} />
      )}
    </div>
  )
}
